<script setup lang="ts">
import {
  PriceCalendar,
  PriceCalendarTileType,
} from '@apps/main/new-desktop/modules/priceCalendar';

import { AppButton, AppFlicker, AppModal } from '@libs/ui';

import type {
  PeekPriceModalCalendar,
  PeekPriceModalHotelOnlyItinerary,
} from '../../models';

import PeekPriceHotelOnlyCard from './PeekPriceHotelOnlyCard.vue';

interface PeekPriceModalDesktopProps {
  itinerary: PeekPriceModalHotelOnlyItinerary;
  calendar: PeekPriceModalCalendar;
}

const props = defineProps<PeekPriceModalDesktopProps>();

interface PeekPriceModalDesktopEmits {
  (eventName: 'cancel'): void;
  (eventName: 'confirm'): void;
  (eventName: 'selectDate', date: Date): void;
  (eventName: 'selectMonth', date: Date): void;
}

const emit = defineEmits<PeekPriceModalDesktopEmits>();
</script>

<template>
  <AppModal @close="emit('cancel')">
    <div class="peek-price-modal-desktop">
      <div class="peek-price-modal-desktop-body">
        <div class="body-details">
          <PeekPriceHotelOnlyCard :itinerary="itinerary" />
        </div>
        <div class="body-separator" />
        <div class="body-calendar">
          <PriceCalendar
            v-if="props.calendar.priceCalendarState.anyResultsLoaded()"
            :selected-month="props.calendar.selectedMonth"
            :months="props.calendar.months"
            :price-calendar-state="props.calendar.priceCalendarState"
            :allow-selection-for="[
              PriceCalendarTileType.CurrentMonthAvailableDay,
            ]"
            header-font="font-label-xl-light"
            @select-month="emit('selectMonth', $event)"
            @select-date="emit('selectDate', $event)"
          />
          <AppFlicker
            v-else
            height="spacing-32"
            width="spacing-80"
            border-radius="border-radius-12"
            class="flicker"
          />
        </div>
      </div>
      <div class="peek-price-modal-desktop-footer">
        <div class="footer-summary">
          <div class="summary-text">
            {{ $t('planner.total_trip_price_for') }}
            {{ props.itinerary.paxes }}, {{ props.itinerary.dates }}
          </div>
          <div class="price-text">{{ props.itinerary.price }}</div>
        </div>
        <div class="footer-actions">
          <AppButton
            type="secondary"
            state="enabled"
            size="large"
            @click="emit('cancel')"
          >
            {{ $t('planner.close') }}
          </AppButton>
          <AppButton
            type="primary"
            state="enabled"
            size="large"
            @click="emit('confirm')"
          >
            {{ $t('planner.select') }}
          </AppButton>
        </div>
      </div>
    </div>
  </AppModal>
</template>

<style scoped lang="scss">
.peek-price-modal-desktop {
  height: 100%;

  display: grid;
  grid-template-rows: 1fr min-content;

  .peek-price-modal-desktop-body {
    padding: var(--spacing-24);

    display: grid;
    grid-template-columns: 1fr var(--spacing-24) 1px var(--spacing-24) 1fr;
    grid-template-rows: auto;

    .body-details {
      grid-column: 1/2;
    }

    .body-separator {
      grid-column: 3/4;
      height: 100%;
      width: 1px;
      background-color: var(--color-neutral-20);
    }

    .body-calendar {
      grid-column: 5/6;
      height: 100%;
    }
  }

  .peek-price-modal-desktop-footer {
    border-top: 1px solid var(--color-neutral-20);
    padding: var(--spacing-16) var(--spacing-24) var(--spacing-16)
      var(--spacing-24);

    display: flex;

    .footer-summary {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .summary-text {
        @include font-label-l-light;
        color: var(--color-neutral-70);
      }

      .price-text {
        @include font-heading-3;
        color: var(--color-soft-black);
      }
    }

    .footer-actions {
      display: flex;
      gap: var(--spacing-24);
    }
  }
}
</style>
